<script setup lang="ts">
import SpIcon from './SpIcon.vue';
import { toasts, dismissToast, type ToastTone } from '../toast';

/**
 * The toast region.
 *
 * Mounted once at the app root. Uses `aria-live="polite"` rather than `assertive` so
 * announcements queue behind whatever the user is doing instead of interrupting them
 * mid-sentence; errors are still visually prominent.
 */
const TONES: Record<
  ToastTone,
  { classes: string; icon: 'success' | 'warning' | 'error' | 'info' }
> = {
  success: { classes: 'border-success-border bg-success-subtle text-success', icon: 'success' },
  error: { classes: 'border-danger-border bg-danger-subtle text-danger', icon: 'error' },
  warning: { classes: 'border-warning-border bg-warning-subtle text-warning', icon: 'warning' },
  info: { classes: 'border-info-border bg-info-subtle text-info', icon: 'info' },
};
</script>

<template>
  <div
    class="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
    role="region"
    aria-live="polite"
    aria-label="Notifications"
  >
    <TransitionGroup
      enter-active-class="transition-all duration-200"
      leave-active-class="transition-all duration-150 absolute"
      enter-from-class="opacity-0 translate-x-4"
      leave-to-class="opacity-0 translate-x-4"
      move-class="transition-transform duration-200"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="pointer-events-auto flex items-start gap-3 rounded-lg border bg-surface-overlay p-3.5 shadow-lg backdrop-blur"
        :class="TONES[toast.tone].classes"
      >
        <SpIcon :name="TONES[toast.tone].icon" :size="17" class="mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-ink">{{ toast.title }}</p>
          <p v-if="toast.description" class="mt-0.5 break-words text-xs text-ink-secondary">
            {{ toast.description }}
          </p>
        </div>
        <button
          type="button"
          class="-mr-1 -mt-1 grid h-6 w-6 shrink-0 place-items-center rounded text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
          aria-label="Dismiss notification"
          @click="dismissToast(toast.id)"
        >
          <SpIcon name="close" :size="13" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>
